<template>
  <AdminLayout>
    <div class="max-w-6xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">매거진 관리</h1>
            <p class="text-sm text-slate-500">인스타그램 포스트를 등록하여 홈 화면에 노출합니다</p>
          </div>
        </div>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          매거진 추가
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">로딩 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="magazines.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-600 mb-2">등록된 매거진이 없습니다</h3>
        <p class="text-slate-400 mb-4">인스타그램 포스트를 등록해보세요.</p>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          첫 매거진 추가하기
        </button>
      </div>

      <!-- 매거진 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="(magazine, index) in magazines"
          :key="magazine.id"
          class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:border-slate-200 transition flex items-center gap-5"
        >
          <!-- 썸네일 -->
          <div class="w-20 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
            <img v-if="magazine.imageUrl" :src="magazine.imageUrl" :alt="magazine.title" class="w-full h-full object-cover">
            <div v-else class="w-full h-full flex items-center justify-center text-slate-400">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
          </div>

          <!-- 정보 -->
          <div class="flex-1 min-w-0">
            <h3 class="font-semibold text-slate-800 truncate mb-1">{{ magazine.title }}</h3>
            <p class="text-sm text-slate-500 mb-2">{{ magazine.date }}</p>
            <a :href="magazine.linkUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-xs text-pink-500 hover:text-pink-600">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
              인스타그램에서 보기
            </a>
          </div>

          <!-- 상태 & 순서 -->
          <div class="flex flex-col items-center gap-2">
            <span :class="[
              'px-2 py-1 text-xs font-medium rounded-full',
              magazine.isActive ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'
            ]">
              {{ magazine.isActive ? '활성' : '비활성' }}
            </span>
            <span class="text-xs text-slate-400">순서: {{ index + 1 }}</span>
          </div>

          <!-- 액션 버튼 -->
          <div class="flex items-center gap-3">
            <!-- 순서 변경 -->
            <div class="flex flex-col gap-1">
              <button
                @click="moveUp(magazine, index)"
                :disabled="index === 0"
                class="p-1.5 bg-slate-100 rounded-md text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                </svg>
              </button>
              <button
                @click="moveDown(magazine, index)"
                :disabled="index === magazines.length - 1"
                class="p-1.5 bg-slate-100 rounded-md text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
            </div>

            <!-- 활성화 토글 -->
            <button
              @click="handleToggleActive(magazine)"
              :class="[
                'relative w-11 h-6 rounded-full transition-colors',
                magazine.isActive ? 'bg-blue-600' : 'bg-slate-200'
              ]"
            >
              <span :class="[
                'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                magazine.isActive ? 'translate-x-5' : 'translate-x-0'
              ]"></span>
            </button>

            <!-- 수정 -->
            <button
              @click="openEditModal(magazine)"
              class="p-2 bg-slate-100 rounded-lg text-slate-500 hover:bg-blue-100 hover:text-blue-600 transition"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>

            <!-- 삭제 -->
            <button
              @click="handleDelete(magazine)"
              class="p-2 bg-slate-100 rounded-lg text-slate-500 hover:bg-red-100 hover:text-red-600 transition"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" @click.self="closeModal">
            <div class="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <!-- 모달 헤더 -->
              <div class="flex items-center justify-between p-5 border-b border-slate-100">
                <h2 class="text-lg font-semibold text-slate-800">{{ isEditing ? '매거진 수정' : '매거진 추가' }}</h2>
                <button @click="closeModal" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <!-- 모달 바디 -->
              <div class="p-5 space-y-5">
                <!-- 이미지 업로드 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">이미지 (4:5 비율 권장)</label>
                  <div
                    @click="$refs.imageInput.click()"
                    :class="[
                      'w-full aspect-[4/5] border-2 border-dashed rounded-xl cursor-pointer overflow-hidden transition',
                      imagePreview ? 'border-slate-200' : 'border-slate-200 hover:border-blue-400 bg-slate-50 hover:bg-blue-50'
                    ]"
                  >
                    <img v-if="imagePreview" :src="imagePreview" alt="미리보기" class="w-full h-full object-cover">
                    <div v-else class="w-full h-full flex flex-col items-center justify-center text-slate-400">
                      <svg class="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <p class="text-sm">클릭하여 이미지 업로드</p>
                      <p class="text-xs text-slate-300 mt-1">인스타그램 이미지 비율: 4:5</p>
                    </div>
                  </div>
                  <input
                    ref="imageInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleImageChange"
                  >
                </div>

                <!-- 제목 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">제목</label>
                  <input
                    v-model="form.title"
                    type="text"
                    placeholder="매거진 제목을 입력하세요"
                    class="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  >
                </div>

                <!-- 인스타그램 링크 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">인스타그램 링크</label>
                  <input
                    v-model="form.linkUrl"
                    type="url"
                    placeholder="https://instagram.com/p/..."
                    class="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  >
                  <p class="text-xs text-slate-400 mt-1">인스타그램 게시물 URL을 입력하세요</p>
                </div>

                <!-- 날짜 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">게시 날짜</label>
                  <input
                    v-model="form.date"
                    type="date"
                    class="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  >
                </div>
              </div>

              <!-- 모달 푸터 -->
              <div class="flex gap-3 p-5 border-t border-slate-100">
                <button
                  @click="closeModal"
                  class="flex-1 py-3 bg-slate-100 text-slate-600 font-medium rounded-xl hover:bg-slate-200 transition"
                >
                  취소
                </button>
                <button
                  @click="handleSubmit"
                  :disabled="isSubmitting || !isFormValid"
                  class="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span v-if="isSubmitting" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  {{ isEditing ? '수정하기' : '추가하기' }}
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useMagazine } from '@/composables/main/useMagazine'

export default {
  name: 'MagazineManageView',
  components: {
    AdminLayout
  },
  setup() {
    const {
      magazines,
      loading,
      getAllMagazines,
      createMagazine,
      updateMagazine,
      deleteMagazine,
      updateOrder,
      toggleActive
    } = useMagazine()

    const showModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)
    const isSubmitting = ref(false)
    const imageFile = ref(null)
    const imagePreview = ref('')

    const form = reactive({
      title: '',
      linkUrl: '',
      date: new Date().toISOString().split('T')[0]
    })

    const isFormValid = computed(() => {
      return form.title && form.linkUrl && (imagePreview.value || isEditing.value)
    })

    const loadData = async () => {
      await getAllMagazines()
    }

    onMounted(loadData)

    const openCreateModal = () => {
      isEditing.value = false
      editingId.value = null
      form.title = ''
      form.linkUrl = ''
      form.date = new Date().toISOString().split('T')[0]
      imageFile.value = null
      imagePreview.value = ''
      showModal.value = true
    }

    const openEditModal = (magazine) => {
      isEditing.value = true
      editingId.value = magazine.id
      form.title = magazine.title
      form.linkUrl = magazine.linkUrl
      form.date = magazine.date
      imageFile.value = null
      imagePreview.value = magazine.imageUrl
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
    }

    const handleImageChange = (e) => {
      const file = e.target.files[0]
      if (file) {
        imageFile.value = file
        imagePreview.value = URL.createObjectURL(file)
      }
    }

    const handleSubmit = async () => {
      if (!isFormValid.value) return

      isSubmitting.value = true

      try {
        if (isEditing.value) {
          await updateMagazine(editingId.value, form, imageFile.value)
        } else {
          await createMagazine(form, imageFile.value)
        }
        await loadData()
        closeModal()
      } catch (err) {
        console.error('저장 실패:', err)
        alert('저장에 실패했습니다.')
      } finally {
        isSubmitting.value = false
      }
    }

    const handleDelete = async (magazine) => {
      if (!confirm(`"${magazine.title}" 매거진을 삭제하시겠습니까?`)) return

      const result = await deleteMagazine(magazine.id)
      if (result.success) {
        await loadData()
      } else {
        alert('삭제에 실패했습니다.')
      }
    }

    const handleToggleActive = async (magazine) => {
      await toggleActive(magazine.id, !magazine.isActive)
    }

    const moveUp = async (magazine, index) => {
      if (index === 0) return

      const prevMagazine = magazines.value[index - 1]
      const currentOrder = magazine.order
      const prevOrder = prevMagazine.order

      await updateOrder(magazine.id, prevOrder)
      await updateOrder(prevMagazine.id, currentOrder)
      await loadData()
    }

    const moveDown = async (magazine, index) => {
      if (index === magazines.value.length - 1) return

      const nextMagazine = magazines.value[index + 1]
      const currentOrder = magazine.order
      const nextOrder = nextMagazine.order

      await updateOrder(magazine.id, nextOrder)
      await updateOrder(nextMagazine.id, currentOrder)
      await loadData()
    }

    return {
      magazines,
      loading,
      showModal,
      isEditing,
      isSubmitting,
      form,
      imagePreview,
      isFormValid,
      openCreateModal,
      openEditModal,
      closeModal,
      handleImageChange,
      handleSubmit,
      handleDelete,
      handleToggleActive,
      moveUp,
      moveDown
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
